import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import apiClient from '@/core/api/client';
import { API_ENDPOINTS } from '@/core/api/endpoints';

export interface UserProfile {
    id: string;
    email: string;
    full_name: string;
    role: string;
    /** Which app the user works in, resolved by the backend from their role. */
    portal?: 'superadmin' | 'consultancy' | 'agent' | 'student';
    /** Custom role giving this user's default permissions (platform staff, agents, students). */
    access_role?: string | null;
    tenant_id?: string | null;
    /** Display name of the consultancy organisation this user belongs to. */
    consultancy_name?: string | null;
    /** Logo URL of the consultancy this user belongs to. */
    consultancy_logo?: string | null;
    permissions: string[];
    is_active: boolean;
    phone?: string | null;
    created_at: string;
}

interface AuthContextType {
    user: UserProfile | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<UserProfile>;
    logout: () => void;
    checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<UserProfile | null>(null);
    const [token, setToken] = useState<string | null>(localStorage.getItem('auth_token'));
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const checkAuth = async () => {
        const storedToken = localStorage.getItem('auth_token');
        if (!storedToken || storedToken === 'undefined' || storedToken === 'null') {
            setUser(null);
            setToken(null);
            setIsLoading(false);
            return;
        }

        try {
            const res = await apiClient.get(API_ENDPOINTS.AUTH.ME);
            // Handle standard API response format { success: true, data: { ... } }
            const userData = res.data?.data || res.data;
            setUser(userData);
            setToken(storedToken);
        } catch (err) {
            console.error('Failed to validate auth session', err);
            localStorage.removeItem('auth_token');
            localStorage.removeItem('refresh_token');
            setUser(null);
            setToken(null);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        checkAuth();
    }, []);

    // Permissions can be changed by an admin while this user is signed in: re-read the profile when the tab
    // regains focus and once a minute, so the menu, buttons and route guards follow without a new login.
    useEffect(() => {
        if (!user) return;
        const refresh = async () => {
            if (document.visibilityState !== 'visible' || !localStorage.getItem('auth_token')) return;
            try {
                const res = await apiClient.get(API_ENDPOINTS.AUTH.ME);
                const fresh: UserProfile = res.data?.data || res.data;
                setUser(prev => (JSON.stringify(prev) === JSON.stringify(fresh) ? prev : fresh));
            } catch {
                // a failed refresh keeps the current session; the API client handles expired tokens
            }
        };
        const timer = window.setInterval(refresh, 60_000);
        window.addEventListener('focus', refresh);
        document.addEventListener('visibilitychange', refresh);
        return () => {
            window.clearInterval(timer);
            window.removeEventListener('focus', refresh);
            document.removeEventListener('visibilitychange', refresh);
        };
    }, [!!user]);

    const login = async (email: string, password: string): Promise<UserProfile> => {
        setIsLoading(true);
        try {
            const res = await apiClient.post(API_ENDPOINTS.AUTH.LOGIN, { email, password });
            
            // Safely extract tokens regardless of wrapper
            const payload = res.data?.data || res.data;
            const accessToken = payload.access_token;
            const refreshToken = payload.refresh_token;

            if (!accessToken) {
                throw new Error('Access token was not returned by backend');
            }

            localStorage.setItem('auth_token', accessToken);
            if (refreshToken) {
                localStorage.setItem('refresh_token', refreshToken);
            }
            setToken(accessToken);

            // Fetch user profile immediately
            const profileRes = await apiClient.get(API_ENDPOINTS.AUTH.ME);
            const userProfile = profileRes.data?.data || profileRes.data;
            setUser(userProfile);
            return userProfile;
        } catch (error) {
            setUser(null);
            setToken(null);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = async () => {
        try {
            // Ends this device's session on the server, so the saved refresh token can no longer be used.
            await apiClient.post(API_ENDPOINTS.AUTH.LOGOUT, { refresh_token: localStorage.getItem('refresh_token') });
        } catch (err) {
            console.warn('Failed to call backend logout endpoint', err);
        } finally {
            localStorage.clear();
            sessionStorage.clear();
            setUser(null);
            setToken(null);
            window.history.replaceState(null, '', '/login');
            window.location.href = '/login';
        }
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                token,
                isAuthenticated: !!user && !!token,
                isLoading,
                login,
                logout,
                checkAuth
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
